/**
 * Basic proxy for BetSlip API.
 *
 * @module app/components/BetSlipAdapter
 */
import _ from 'lodash';
import jQuery from 'jquery';
import MessageBusTopics from '../../common/MessageBusTopics';
import SportsDataRepository from './SportsDataRepository';
import constant from '../constants/config';

/**
 * Selections count
 *
 * @type {Number}
 */
var selnCount;

/**
 * List of topics for messageBus communication
 *
 * @type    {Object}
 *
 * @private
 */
var messageBusTopics = {
    getBets: 'BetslipAdapter.getBets',
    betslipClosed: 'betslip.closed',
};

/**
 * Parameters used when checking for sportsbookAPI
 *
 * @type {Object}
 */
var checkApi = {
    delay: 100,
    maxTries: 100,
};

/**
 * Betslip API.
 *
 * @type {Object}
 *
 * @private
 */
var sportsbookAPI = null;

/**
 * Initialize the betslip module
 *
 * @returns {undefined}
 *
 * @private
 */
var initializeModule = function initializeModule() {

    subscribeToEvents();
    displayCounts();
};

/**
 * Whether or not the betslip API has been included.
 *
 * @returns {Boolean} HasAPI flag
 *
 * @private
 */
var hasAPI = function () {

    return (
        sportsbookAPI !== null &&
        typeof sportsbookAPI !== 'undefined' &&
        typeof sportsbookAPI.betslipAPI !== 'undefined' &&
        typeof sportsbookAPI.betslipAPI.regfuncAPI !== 'undefined'
    );
};

/**
 * Determine whether eachway is available for a market.
 *
 * @param {Object} market The market.
 *
 * @returns {Object} containing each-way availability
 *
 * @private
 */
var getAvail = function (market) {

    return {

        ewAvail: (market && market.eachWay && market.eachWay.toString() === 'true') &&
        (parseInt(getPlaces(market).ewPlaces, 10) > 1) ? 'Y' : 'N', // _ew_avail, from market, Y or N
    };
};

/**
 * Get the eachway places for a market.
 *
 * @param {Object} market The market.
 *
 * @returns {Object} object containing each-way places
 *
 * @private
 */
var getPlaces = function (market) {

    var ewPlaces = '';

    if (market && market.eachWayPlaces) {

        ewPlaces = getNumericalPrice(market.eachWayPlaces);
    }

    return {
        ewPlaces: ewPlaces, // _ew_places, from market
    };
};

/**
 * Converts string to number or returns empty string
 *
 * @param {String} price Price
 *
 * @returns {Number} price Price as a number or empty string
 *
 * @private
 */
var getNumericalPrice = function (price) {

    return +price || '';
};

/**
 * Get the nominator for a market.
 *
 * @param {Object} market The market
 *
 * @returns {Object} Object containing each-way numerator
 *
 * @private
 */
var getNominator = function (market) {

    var eachWayFactorNum = '';

    if (market && market.eachWayFactorNum) {

        eachWayFactorNum = getNumericalPrice(market.eachWayFactorNum);
    }

    return {
        ewNumerator: eachWayFactorNum, // _ew_fac_num, from market
    };
};

/**
 * Get the denominator for a market.
 *
 * @param {Object} market The market
 *
 * @returns {Object} Object containing each-way denominator
 *
 * @private
 */
var getDenominator = function (market) {

    var eachWayFactorDen = '';

    if (market) {

        eachWayFactorDen = getNumericalPrice(market.eachWayFactorDen);
    }

    return {
        ewDenominator: eachWayFactorDen, // _ew_fac_den, from market
    };
};

/**
 * Get the handicap value for a market.
 *
 * @param {Object} market The market.
 *
 * @returns {Object} Object containing handicap value
 *
 * @private
 */
var getHandicapValue = function (market) {

    if (market && market.hasOwnProperty('hcapValue') &&
        parseFloat(market.hcapValue).toFixed(1)) {

        return {
            handicapValue: parseFloat(market.hcapValue).toFixed(1),
        };
    }

    return {
        handicapValue: '',
    };
};

/**
 * Get the eachway terms for a market
 *
 * @param {Object} market The market
 *
 * @returns {Object} data Each Way Terms
 *
 * @private
 */
var getEachWay = function (market) {

    /**
     * Eachway data.
     *
     * @type {Object}
     *
     * @private
     */
    var data = _.extend({},
        getAvail(market),
        getPlaces(market),
        getNominator(market),
        getDenominator(market));

    return data;
};

/**
 * Get the various values for a market
 *
 * @param {Object} market The market
 *
 * @returns {Object} marketData Market's data
 *
 * @private
 */
var getMarketValues = function (market) {

    /**
     * Market data.
     *
     * @type {Object}
     */
    var marketData;

    marketData = _.extend({},
        getHandicapValue(market),
        getEachWay(market));
    return marketData;
};

/**
 * Get the handicap value for a leg
 *
 * @param {Object} leg Leg
 *
 * @returns {Object} handicapValue Object containing handicap value
 *
 * @private
 */
var getHandicap = function (leg) {

    var handicapValue = parseFloat(leg.handicapValue).toFixed(1);

    if (handicapValue === 'NaN') {
        return {
            handicapValue: 0,
        };
    } else {
        return {
            handicapValue: handicapValue,
            legSort: '',
        };
    }
};

/**
 * Returns leg sort if cast market
 *
 * @param {Object} market Market
 *
 * @returns {String} letSort Leg Sort
 *
 * @private
 */
var getLegSort = function (market) {

    if (market && market.sort && _.indexOf(['SC', 'AC', 'LC', 'FW', 'LW', 'AW'], market.sort) !== -1) {

        return market.sort;
    }

    return '--';
};

/**
 * Obtains bet IDs from BetslipAPI and publishes message about that
 *
 * @returns {void}
 *
 * @private
 */
var getBetsFromApi = function getBetsFromApi() {

    var bets = sportsbookAPI.betslipAPI.getBets();
    var betIds = [];

    if (bets && bets[0] === true && bets.length >= 2) {

        bets = JSON.parse(bets[1]);
        betIds = _.map(bets.singles, 'ev_oc_id');
        window.WH.messageBus.publish(messageBusTopics.getBets, betIds);

        jQuery('body').removeClass('betslip-loading');
    }
};

/**
 * Process a regular leg.
 *
 * @param {Object} selectionModel Selection.
 * @param {Object} market The market.
 * @param {Object} event The event.
 *
 * @returns {Object} leg Leg data
 *
 * @private
 */
var processRegularLeg = function (selectionModel, market, event) {

    // Mapping market & selection model values to BetSlip API arguments
    var leg = {
        legSort: getLegSort(market), // _leg_sort
        priceType: 'L', // _price_type
        birIndex: '', // _bir_index, default blank
        lpAvail: '', // _lp_avail, default blank
        selectionId: selectionModel.id.replace('OB_OU', ''), // _ev_oc_id
        priceNumerator: getNumericalPrice(selectionModel.priceNumerator), // _lp_num
        priceDenominator: getNumericalPrice(selectionModel.priceDenominator), // _lp_den
        tracking: selectionModel.tracking,
        eventId: event.id,
        eventType: event.sportId,
        eventStartTime: event.startDateTime,
    };

    leg = _.extend(leg, getMarketValues(market));
    leg = _.extend(leg, getHandicap(leg));

    return leg;
};

/**
 * Process an SP leg.
 *
 * @param {Object} selectionModel Selection
 * @param {Object} market The market
 * @param {Object} event The event.
 *
 * @returns {Object} leg Leg data
 *
 * @private
 */
var processStartingPriceLeg = function (selectionModel, market, event) {

    // Mapping market & selection model values to BetSlip API arguments
    var leg = {
        legSort: getLegSort(market), // _leg_sort
        priceType: 'S', // _price_type
        birIndex: '', // _bir_index, default blank
        lpAvail: '', // _lp_avail, default blank
        selectionId: selectionModel.id.replace('OB_OU', '').replace('_SP', ''), // _ev_oc_id
        priceNumerator: getNumericalPrice(selectionModel.priceNumerator), // _lp_num
        priceDenominator: getNumericalPrice(selectionModel.priceDenominator), // _lp_den
        tracking: selectionModel.tracking,
        eventId: event.id,
        eventType: event.sportId,
        eventStartTime: event.startDateTime,
    };

    leg = _.extend(leg, getMarketValues(market));
    leg = _.extend(leg, getHandicap(leg));

    return leg;
};

/**
 * Updates the selections according to data coming from Betslip. Runs after the timeout threshold
 * is reached in the subscribeToEvents.onBetSlipUpdate
 *
 * See SBK-3318 and subscribeToEvents.onBetSlipUpdate for more info
 *
 * @param {String} topic Topic
 * @param {Array} selectionsTracker Selections Tracker
 *
 * @returns {void}
 *
 * @private
 */
var delayedBetslipUpdate = function delayedBetslipUpdate(topic, selectionsTracker) {

    /**
     * Run callbacks from api.onUpdate elements
     *
     * @param {Function} callback Callback
     *
     * @returns {void}
     *
     * @private
     */
    var doCallback = function doCallback(callback) {

        if (_.isFunction(callback)) {

            _.each(selectionsTracker, function (added, selectionId) {

                callback(selectionId, added);
            });
        }
    };

    _.each(api.onUpdate, doCallback);
};

/**
 * Subscribe to events.
 *
 * @returns {void}
 *
 * @private
 */
var subscribeToEvents = function () {

    /**
     * Betslip close listener
     *
     * @returns     {void}
     *
     * @private
     */
    var betslipCloseListener = function () {

        jQuery('#close-betslip').off('click.betslipClosing').on('click.betslipClosing', function () {

            window.WH.messageBus.publish(messageBusTopics.betslipClosed);
            delayedDisplayCounts();
        });
    };

    window.WH.messageBus.subscribe(
        MessageBusTopics.BETSLIP_ADAPTER_SELECTION_UPDATE_COMPLETED,
        delayedBetslipUpdate
    );
    betslipCloseListener();
};

/**
 * Contains the list of DOM elements to be used and their selectors.
 *
 * @type {Object}
 *
 * @private
 */
var DOMElements = {

    betSlipCount: {
        element: null,
        selector: '#betslip-count',
    },

    mobileBetSlipCount: {
        element: null,
        selector: '#mobile-betslip-count',
    },

    openBetCount: {
        element: null,
        selector: '#openbet-count',
    },

    mobileOpenBetCount: {
        element: null,
        selector: '#mobile-openbet-count',
    },

    toolbarBetslipBtn: {
        element: null,
        selector: '#betslip-btn-toolbar',
    },
};

/**
 * Contains the list of css classes we are going to need animations
 *
 * @type {Object}
 *
 * @private
 */
var cssClasses = {

    toolbarBetslipBtnPulse: 'toolbar__item--deep-beat',
    toolbarCounterIn: 'toolbar__badge--fly-in',
    toolbarCounterOut: 'toolbar__badge--fly-out',
};

/**
 * A helper method to get the element and caching its value in the DOMElements object structure
 *
 * @param {Object} object DOM element
 *
 * @returns {Object} object DOM element
 *
 * @private
 */
var getElement = function getElement(object) {

    if (object.element === null) {

        object.element = jQuery(object.selector);
    }

    return object.element;
};

/**
 * Displays betslip count
 *
 * @param {number} count Betslip Count
 *
 * @returns {void}
 *
 * @private
 */
var displayBetSlipCount = function (count) {

    count = (count === 0) ? '' : count;
    getElement(DOMElements.betSlipCount)
        .text(count).addClass('force-render-view').removeClass('force-render-view');
    getElement(DOMElements.mobileBetSlipCount)
        .text(count).addClass('force-render-view').removeClass('force-render-view');
    updateBetslipButtonClasses(count);
};

/**
 * Displays open bets count if truthy value is found
 *
 * @param {number} count Open Bets count
 *
 * @returns {void}
 *
 * @private
 */
var displayOpenBetsCount = function (count) {

    count = (count === 0) ? '' : count;
    getElement(DOMElements.openBetCount)
        .text(count).addClass('force-render-view').removeClass('force-render-view');
    getElement(DOMElements.mobileOpenBetCount)
        .text(count).addClass('force-render-view').removeClass('force-render-view');
    updateOpenbetsButtonClasses(count);
};

/**
 * method to display counts of open bets and betslip items
 *
 * @returns {void}
 *
 * @private
 */
var displayCounts = function () {

    selnCount = checkNaN(parseInt(api.getBetCount(), 10));
    var obCount = checkNaN(parseInt(api.getOpenBets(), 10));

    displayBetSlipCount(selnCount);
    displayOpenBetsCount(obCount);
};

/**
 * Method to check for and avoid NaNs
 *
 * @param {number} number Number
 *
 * @returns {number} number Number
 *
 * @private
 */
var checkNaN = function (number) {

    return (isNaN(number)) ? 0 : number;
};

/**
 * Animates counter icon with number count of the betslip toolbar
 * button, reflow the element to restart the animation.
 *
 * @param {object} animation Animation to be applied, fly effect in or out
 *
 * @returns {void}
 *
 * @private
 */
var animateCounter = function (animation) {
    var counter = getElement(DOMElements.mobileBetSlipCount);

    counter.removeClass(cssClasses.toolbarCounterIn).removeClass(cssClasses.toolbarCounterOut);
    //Reflow element in order to restart the animation.
    counter.outerWidth();
    counter.addClass(animation);
};

/**
 * Animates toolbar betslip button with deep beat effect,
 * reflow the element to restart the animation.
 *
 * @returns {void}
 *
 * @private
 */
var animateToolbarBetslipBtn = function () {

    var button = getElement(DOMElements.toolbarBetslipBtn);

    button.removeClass(cssClasses.toolbarBetslipBtnPulse);
    //Reflow element in order to restart the animation.
    button.outerWidth();
    button.addClass(cssClasses.toolbarBetslipBtnPulse);
};

var updateBetslipButtonClasses = function (count) {

    var mobileBetSlipCount = getElement(DOMElements.mobileBetSlipCount);

    if (count > 0) {

        mobileBetSlipCount.removeClass('displayNone');
    } else {

        mobileBetSlipCount.addClass('displayNone');
    }
};

var updateOpenbetsButtonClasses = function (count) {

    var mobileBetSlipCount = getElement(DOMElements.mobileOpenBetCount);

    if (count > 0) {

        mobileBetSlipCount.removeClass('displayNone');
    } else {

        mobileBetSlipCount.addClass('displayNone');
    }
};

/**
 * Update the counters after a delay.
 *
 * @returns {void}
 *
 * @private
 */
var delayedDisplayCounts = function () {

    window.setTimeout(displayCounts, 250);
};

/**
 * Reload the betslip.
 *
 * @returns {void}
 *
 * @private
 */
var reallyReloadBetslip = function () {

    //document.betslip.change_slip_mode('B','GoBets');

    api.updateSidebarsPosition('open');
};

/**
 * Get the leg values based on price type
 *
 * @param {Object} selectionModel   Selection.
 * @param {Object} market           Market
 * @param {Object} event            Event
 *
 * @return {void}
 *
 * @private
 */
var getLegValues = function (selectionModel, market, event) {

    if (selectionModel.odds === 'SP') {
        return processStartingPriceLeg(selectionModel, market, event);
    } else {
        return processRegularLeg(selectionModel, market, event);
    }
};

/**
 * Checks if login requires a refresh.
 *
 * @TODO: This is a temporal patch which is covered in EMA-664.
 * This component will be cleaned up in the next few phases and
 * these feature switches removed.
 *
 * @returns {Boolean} isLoginWithoutRefreshEnabled
 *
 * @private
 */
var isLoginWithoutRefreshEnabled = function isLoginWithoutRefreshEnabled() {

    if (window.MyAccount &&
        window.MyAccount.loginWithoutRefreshEnabled &&
        window.MyAccount.loginWithoutRefreshEnabled.toString() === 'true') {

        return window.MyAccount.loginWithoutRefreshEnabled.toString() === 'true';
    }

    return false;
};

/**
 * Public API.
 *
 * @type {Object}
 *
 * @private
 */
var api = {

    /**
     * Update callbacks.
     *
     * @type {Array}
     *
     * @public
     */
    onUpdate: [],

    /**
     * Composes the data needed to place a leg
     *
     * @param   {Object}    selectionModel  Preliminary model needed to compose the leg.
     *
     * @returns {Object}    Leg object
     */
    composeLeg: function composeLeg(selectionModel) {
        var model = SportsDataRepository.getModel();
        var selection = model.selections[selectionModel.id] || selectionModel;
        var market = model.markets[selectionModel.marketId];
        var event = model.events[selection.eventId] || {};
        event.id = event.id || selection.eventId;
        return _.extend(
            getLegValues(selectionModel, market, event),
            {
                analytics: selectionModel.analytics,
            }
        );
    },

    /**
     * Add a selection to the betslip.
     *
     * @param {Object} selectionModel Selection.
     *
     * @returns {void}
     *
     * @public
     */
    addToBetSlip: function (selectionModel) {

        var leg = api.composeLeg(selectionModel);
        window.WH.messageBus.publish(MessageBusTopics.BETSLIP_LEG_ADD, leg);
    },

    /**
     * Applies animation when bet is added, callback of 'addBet' event
     * subscription.
     *
     * @returns {void}
     *
     * @public
     */
    animationBetAdded: function () {

        animateCounter(cssClasses.toolbarCounterIn);
        animateToolbarBetslipBtn();
    },

    /**
     * Applies animation when bet is removed, callback of 'removeBet' event
     * subscription.
     *
     * @returns {void}
     *
     * @public
     */
    animationBetRemoved: function animationBetRemoved() {

        animateCounter(cssClasses.toolbarCounterOut);
    },

    /**
     * Increments the selections counts and displays animation
     *
     * @returns {void}
     *
     * @public
     */
    animatedSelectionCountIncrement: function animatedSelectionCountIncrement() {

        displayBetSlipCount(++selnCount);
        api.animationBetAdded();
    },

    /**
     * Decrements selections counts
     *
     * @returns {void}
     *
     * @public
     */
    selectionCountDecrement: function selectionCountDecrement() {

        displayBetSlipCount(--selnCount);
    },

    /**
     * Decrements the selections counts and displays animation
     *
     * @returns {void}
     *
     * @public
     */
    animatedSelectionCountDecrement: function animatedSelectionCountDecrement() {

        displayBetSlipCount(--selnCount);
        api.animationBetRemoved();
    },

    /**
     * Resolution for issue SBK-809.
     * Sidebars (-contextual, -toolbar) overlaps on page content when betslip is open or close
     * and Sidebars are positioned "fixed" (.affix, .affix-bottom)
     *
     * @param {String} action Betslip action (open or close)
     *
     * @return {void}
     *
     * @public
     */
    updateSidebarsPosition: function (action) {

        var delay = (action === 'open' ? 50 : 150);
        jQuery('.-contextual, .-toolbar').find('.affix, .affix-bottom').css('margin-bottom', '1px')
            .delay(delay)
            .queue(function () {
                jQuery(this).css('margin-bottom', '0px');
                jQuery(this).dequeue();
            });
    },

    /**
     * Return the ids of the selections added to the betslip.
     * Reads the DOM of the betslip and returns an array of ids added.
     *
     * @param {Integer} count Retries count
     *
     * @returns {void}
     *
     * @public
     */
    getSelectionsFromBetslip: function getSelectionsFromBetslip(count) {

        if (hasAPI() && sportsbookAPI.betslipAPI.getBets && sportsbookAPI.betslipAPI.getBets()[0] === true) {

            getBetsFromApi();
        } else {

            //In case there is no betslipAPI, retry
            window.setTimeout(function () {

                ++count;

                if (count < checkApi.maxTries) {

                    api.getSelectionsFromBetslip(count);
                }
            }, checkApi.delay + checkApi.delay * count);
        }
    },

    /**
     * Remove a selection from the betslip.
     *
     * @param {Object} selectionModel Selection.
     *
     * @returns {void}
     *
     * @public
     */
    removeFromBetSlip: function (selectionModel) {
        var objectToSend = selectionModel;
        if(window.whStatic.config.jurisdiction === constant.newJerseyName) {
            var leg = api.composeLeg(selectionModel);
            leg.id = selectionModel.id;
            objectToSend = leg;
        }
        window.WH.messageBus.publish(MessageBusTopics.BETSLIP_LEG_REMOVE, objectToSend);
    },

    /**
     * Get the number of singles added to the betslip.
     *
     * @returns {Integer} Number of Singles
     *
     * @public
     */
    getBetCount: function () {

        return (hasAPI()) ? +sportsbookAPI.betslipAPI.getNumberSelections('BS') : 0;
    },

    /**
     * Get the number of open bets on the betslip.
     *
     * @returns {Integer} Number of Open Bets
     *
     * @public
     */
    getOpenBets: function () {

        return (hasAPI()) ? +sportsbookAPI.betslipAPI.getNumberSelections('OB') : 0;
    },

    /**
     * Perform initialisation.
     *
     * @returns {void}
     *
     * @public
     */
    init: function () {

        sportsbookAPI = window.document.sportsbookAPI;

        if (hasAPI() || window.whStatic.config.enableBetSlipAdapterInitializeModule) {

            initializeModule();
        } else {

            window.setTimeout(api.init, 300);
        }
    },

    /**
     * Re-inits the betslip
     *
     * @returns {void}
     *
     * @public
     */
    reInit: function () {

        if (!isLoginWithoutRefreshEnabled()) {

            window.WH.messageBus.publish(MessageBusTopics.BETSLIP_REINIT);
            return;
        }

        window.WH.messageBus.publish(MessageBusTopics.REQUEST_GOT_LOGIN_STATE);
    },

    displayCounts: displayCounts,

    delayedBetslipUpdate: delayedBetslipUpdate,

    /**
     * Reload the betslip, if possible.
     *
     * @returns {void}
     *
     * @public
     */
    reloadBetslip: function () {

        /*jshint -W106 */
        if (window.document.betslip && typeof document.betslip.change_slip_mode === 'function') {
            /*jshint +W106 */
            setTimeout(reallyReloadBetslip, 50);
        }
    },
};

if (window && window.WH && window.WH.messageBus) {

    window.WH.messageBus.subscribe(MessageBusTopics.BETSLIP_INIT_COMPLETE, api.init);
}

export default api;
